import React, { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ChevronDown,
  ClipboardList,
  Bell,
  ShieldCheck,
  Sparkles,
  Wrench,
  CalendarCheck,
  BadgeDollarSign,
  CreditCard,
} from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import {
  marketingColors as colors,
  marketingBrand,
  PAPER,
  BOOKING_CALENDAR_URL,
  marketingEase as ease,
} from '../lib/marketingBrand'
import {
  ACCENT,
  HeroEmboss,
  MKRule,
  MKImageFrame,
  PaperTextureOverlay,
  SketchBreak,
} from '../components/marketing/MarketingChrome'

const IMG = 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'

const btnPrimary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110'
const btnSecondary =
  'group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-500 hover:opacity-70 border'

/* ─────────────── Hero ─────────────── */
const Hero: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  return (
    <section ref={ref} className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden" style={{ backgroundColor: PAPER }}>
      <div className="absolute inset-0">
        <img src={`${IMG}services.jpg`} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 38%, ${PAPER}65 70%, transparent 100%)` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${PAPER}ea 0%, transparent 35%, transparent 72%, ${PAPER}f2 100%)` }} />
      </div>
      <HeroEmboss />

      <div className="relative z-10 pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-20">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.15 }}
            >
              <MKRule wide />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease, delay: 0.2 }}
              className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mt-6 mb-5"
              style={{ color: colors.copper }}
            >
              Full Maintenance Membership
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.25 }}
              className="font-serif font-normal mb-6"
              style={{
                color: colors.tealDark,
                fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
              }}
            >
              Proactive Home Care,{' '}
              <span className="italic" style={{ color: colors.copper }}>
                Directed by Hudson.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.4 }}
              className="font-sans text-lg lg:text-xl leading-[1.85] max-w-2xl mb-10"
              style={{ color: colors.drillbitSkies }}
            >
              A dedicated Hudson does the hands-on work in your home, monitors key systems, maintains your MyHome
              Score™, and brings in licensed specialists when the job demands it. Fully coordinated so minor issues
              never become major repairs.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.55 }}
              className="flex flex-wrap gap-4"
            >
              <a
                href={BOOKING_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={btnPrimary}
                style={{ backgroundColor: colors.copper }}
              >
                Book Your Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <Link
                to="/quiz"
                className={btnSecondary}
                style={{ borderColor: colors.plaster, color: colors.drillbitSkies }}
              >
                Discover Your Archetype
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─────────────── Membership Includes ─────────────── */
const MembershipIncludes: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  const includes = [
    'Monthly in-home maintenance by a dedicated Hudson (our trained technician)',
    'A MyHome Score™ every quarter',
    'VIP priority scheduling for urgent requests',
    'Access to your personal MyHome Vault™ and MyHome Blueprint™',
    'Seasonal prep and expert insights to protect your home\u2019s value',
  ]

  return (
    <section ref={ref} id="membership" className="relative py-24 md:py-32 lg:py-40 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.035} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease }}
          >
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
              What&rsquo;s Included
            </p>
            <h2
              className="font-serif font-normal mb-8"
              style={{
                color: colors.nearBlack,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Everything Your Home Needs.{' '}
              <span className="italic" style={{ color: colors.copper }}>
                Nothing You Have to Track.
              </span>
            </h2>
            <ul className="space-y-4 mb-8">
              {includes.map((item) => (
                <li key={item} className="flex items-start gap-3 font-sans text-base leading-[1.85]" style={{ color: colors.drillbitSkies }}>
                  <span className="mt-2 inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.copper }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="font-sans text-base leading-[1.85]" style={{ color: colors.drillbitSkies }}>
              Membership also includes escalating levels of coverage and white-glove service.
            </p>
            <div className="mt-10">
              <Link to="/pricing" className={btnPrimary} style={{ backgroundColor: colors.copper }}>
                View Pricing
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
          >
            <MKImageFrame src={ACCENT.hudsonConsultation} alt="" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* ─────────────── Who Will Be in My Home (Cassi only) ─────────────── */
const WhoVisitsSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-24 md:py-32 lg:py-40" style={{ backgroundColor: colors.cream }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="text-center mb-12"
        >
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
            Your Hudson
          </p>
          <div className="flex justify-center mb-6"><MKRule /></div>
          <h2
            className="font-serif font-normal"
            style={{
              color: colors.tealDark,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            Who Will Be Coming Into My Home?
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
          className="grid sm:grid-cols-[auto_1fr] gap-10 items-center max-w-3xl mx-auto bg-white p-8 lg:p-10 shadow-sm"
        >
          <div className="flex flex-col items-center">
            <img
              src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/Cassi.png"
              alt={'Cassi Luree \u2014 Director of Operations'}
              className="h-32 w-32 rounded-full object-cover ring-1"
              style={{ borderColor: colors.plaster }}
              loading="lazy"
            />
            <p className="mt-4 font-serif text-lg" style={{ color: colors.tealDark }}>Cassi Luree</p>
            <p className="font-sans text-xs mt-1" style={{ color: colors.copper }}>Director of Operations</p>
          </div>
          <p className="font-sans text-base leading-[1.85]" style={{ color: colors.drillbitSkies }}>
            You&rsquo;ll have one dedicated Hudson assigned to your home. They&rsquo;re fully vetted, trained, and
            professionally equipped to manage everything from HVAC filter changes to identifying hidden risks. Think of
            them as your home&rsquo;s personal technician&mdash;with a touch of Ritz-Carlton.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

/* ─────────────── Service Area Map ─────────────── */
const ServiceAreaSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-24 md:py-32" style={{ backgroundColor: PAPER }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="text-center mb-10"
        >
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
            Service Area
          </p>
          <h2
            className="font-serif font-normal mb-6"
            style={{
              color: colors.tealDark,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            Serving the Des Moines Metro
          </h2>
          <div className="inline-flex flex-wrap items-center justify-center gap-2">
            {['Ankeny', 'Huxley', 'Urbandale', 'West Des Moines', 'Waukee', 'Johnston'].map((c) => (
              <span
                key={c}
                className="px-3 py-1 rounded-full font-sans text-xs font-semibold uppercase tracking-[0.12em] text-white"
                style={{ backgroundColor: colors.teal }}
              >
                {c}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
          className="relative overflow-hidden border"
          style={{ borderColor: `${colors.plaster}` }}
        >
          <iframe
            title="Des Moines Metro Map"
            src="https://www.google.com/maps?q=Des+Moines+Metro,+IA&output=embed"
            style={{ width: '100%', height: '420px', border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  )
}

/* ─────────────── Home Maintenance Reinvented ─────────────── */
const ReinventedSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  const cards = [
    { icon: ClipboardList, title: 'MyHome Vault\u2122', text: 'Your home\u2019s source of truth\u2014records, invoices, and photos in one place.' },
    { icon: Bell, title: 'MyHome Blueprint\u2122', text: 'A personalized, proactive plan built around your systems, seasons, and schedule.' },
    { icon: ShieldCheck, title: 'Preventative Insights', text: 'Home Directors surface early-warning signals with on-site notes and pattern data.' },
    { icon: Sparkles, title: 'MyHome Score\u2122', text: 'Quarterly score highlights momentum, risks, and next best actions.' },
    { icon: Wrench, title: 'Licensed Specialists', text: 'When work needs a trade license, we bring in partners who are insured, background-checked, and performance-reviewed\u2014your Hudson still owns scheduling and follow-through.' },
    { icon: CalendarCheck, title: 'Scheduling & Follow-Through', text: 'Your Hudson visits are on the calendar first. When a pro is required, we line them up, confirm windows, and close the loop\u2014you are not the project manager.' },
    { icon: BadgeDollarSign, title: 'Competitive Quotes', text: 'Transparent quotes with Hudson negotiating leverage when it matters.' },
    { icon: CreditCard, title: 'Simplified Payments', text: 'Approve work and track billing securely from a single dashboard.' },
  ]

  return (
    <section ref={ref} className="py-24 md:py-32 lg:py-40" style={{ backgroundColor: colors.cream }}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="text-center mb-14"
        >
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
            Reinvented
          </p>
          <div className="flex justify-center mb-6"><MKRule /></div>
          <h2
            className="font-serif font-normal"
            style={{
              color: colors.tealDark,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            Home Maintenance, Reinvented.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map(({ icon: Icon, title, text }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease, delay: 0.05 + i * 0.05 }}
              className="bg-white p-6 lg:p-7"
              style={{ border: `1px solid ${colors.plaster}55` }}
            >
              <Icon className="h-6 w-6 mb-4" style={{ color: colors.teal }} />
              <h3
                className="font-serif text-lg mb-2"
                style={{ color: colors.tealDark, lineHeight: 1.2 }}
              >
                {title}
              </h3>
              <p
                className="font-sans text-sm leading-[1.7]"
                style={{ color: colors.drillbitSkies }}
              >
                {text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─────────────── Blog Previews ─────────────── */
const BLOG_POSTS = [
  {
    href: '/blog/iowa-luxury',
    category: 'Homeowners',
    date: 'September 1, 2025',
    title: 'Iowa Homeowners Are Choosing Luxury',
    excerpt:
      'A new generation of Iowa homeowners is treating their homes like the long-term investments they are. Across the Des Moines metro, families are trading reactive handyman calls for proactive, concierge-style care. Here\u2019s why that shift is happening, and what it means for the value of your home.',
  },
  {
    href: '/blog/proactive-maintenance',
    category: 'Pros',
    date: 'February 19, 2025',
    title: 'Why High-Earning Homeowners Are Ditching the Handyman Model',
    excerpt:
      'Time-strapped professionals don\u2019t want a phone tree or another vendor to manage. They want a single, accountable home pro. We unpack why proactive home management is replacing the handyman model in higher-end neighborhoods, and what to look for in a service that gets it right.',
  },
]

const BlogSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id="learn" className="py-24 md:py-32 lg:py-40" style={{ backgroundColor: PAPER }}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12"
        >
          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
              From the Journal
            </p>
            <h2
              className="font-serif font-normal"
              style={{
                color: colors.tealDark,
                fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Learn
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {BLOG_POSTS.map((post, i) => (
            <motion.article
              key={post.href}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.08 }}
              className="bg-white p-8 lg:p-10 flex flex-col"
              style={{ border: `1px solid ${colors.plaster}55` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: colors.copper }}>
                  {post.category}
                </span>
                <span className="h-px flex-1" style={{ backgroundColor: `${colors.plaster}` }} />
                <span className="font-sans text-xs" style={{ color: colors.drillbitSkies }}>
                  {post.date}
                </span>
              </div>
              <h3
                className="font-serif font-normal mb-4"
                style={{
                  color: colors.tealDark,
                  fontSize: 'clamp(1.35rem, 2vw, 1.6rem)',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {post.title}
              </h3>
              <p className="font-sans text-base leading-[1.85] mb-6 flex-1" style={{ color: colors.drillbitSkies }}>
                {post.excerpt}
              </p>
              <Link
                to={post.href}
                className="group inline-flex items-center gap-2 self-start font-sans text-sm font-semibold uppercase tracking-[0.18em] transition-colors"
                style={{ color: colors.teal }}
              >
                Read More
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─────────────── FAQs ─────────────── */
const FAQ_ITEMS = [
  {
    q: 'What is Hudson My Home\u2122?',
    a: 'Hudson My Home\u2122 is a premium home management service for busy, high-income homeowners. We handle monthly maintenance\u2014preventative checks to minor repairs\u2014so you can enjoy your home without the upkeep.',
  },
  {
    q: 'How is this different from a handyman or repair service?',
    a: 'We\u2019re proactive, not reactive. Your Home Director anticipates needs, schedules work in advance, and prevents emergencies with seasonal planning\u2014more like a concierge than an on-call fixer.',
  },
  {
    q: 'What\u2019s included in a membership?',
    a: 'Monthly in-home maintenance, quarterly MyHome Score\u2122, VIP priority for urgent requests, plus access to your MyHome Vault\u2122 and MyHome Blueprint\u2122 with expert seasonal guidance.',
  },
  {
    q: 'Who will be coming into my home?',
    a: 'One dedicated, fully-vetted Hudson. They perform routine maintenance in your home, surface risks early, and bring in trusted vendors when specialized work is needed\u2014still coordinated end to end.',
  },
  {
    q: 'Do you do emergency repairs?',
    a: 'Yes. While we prioritize prevention, active members receive 24/7 emergency response with same-day triage and coordination.',
  },
  {
    q: 'How do I get started?',
    a: 'Start with a consultation from a Hudson Home Consultant\u2122. We assess your home, create your MyHome Blueprint\u2122, and pair you with your Home Director.',
  },
  {
    q: 'What types of maintenance do you handle?',
    a: 'Everything from filters, alarms, and vents to sump pumps, water heaters, and seasonal prep\u2014over 100 tasks annually, tailored to your home.',
  },
  {
    q: 'Can you handle bigger jobs?',
    a: 'Absolutely. Your Hudson handles what they can in-home; for larger or licensed trades, we coordinate vetted pros and oversee quality, scheduling, and follow-up\u2014so you don\u2019t have to.',
  },
  {
    q: 'Is there a contract?',
    a: 'Month-to-month membership. No long-term contracts\u2014just long-term relationships.',
  },
  {
    q: 'Do you serve homes outside Des Moines?',
    a: 'We currently serve the Des Moines Metro. Add your zip via our contact form to be notified as we expand.',
  },
]

const FAQSection: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section ref={ref} id="faqs" className="py-24 md:py-32 lg:py-40" style={{ backgroundColor: colors.cream }}>
      <div className="mx-auto max-w-3xl px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="text-center mb-12"
        >
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: colors.copper }}>
            Questions
          </p>
          <div className="flex justify-center mb-6"><MKRule /></div>
          <h2
            className="font-serif font-normal"
            style={{
              color: colors.tealDark,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            Have Any Questions? Let&rsquo;s Answer Them.
          </h2>
        </motion.div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((faq, i) => (
            <motion.div
              key={faq.q}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.45, ease, delay: 0.05 + i * 0.04 }}
              className="bg-white"
              style={{ border: `1px solid ${colors.plaster}55` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <span className="font-serif text-base sm:text-lg pr-4" style={{ color: colors.tealDark }}>
                  {faq.q}
                </span>
                <ChevronDown
                  className="w-5 h-5 flex-shrink-0 transition-transform duration-300"
                  style={{
                    color: colors.copper,
                    transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 font-sans text-base leading-[1.85]" style={{ color: colors.drillbitSkies }}>
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─────────────── Footer CTA ─────────────── */
const FooterCTA: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-24 md:py-32 lg:py-40" style={{ backgroundColor: colors.tealDark }}>
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="font-serif text-white mb-4"
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          We Maintain Your Home
          <br />
          So You Don&apos;t Have To.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease, delay: 0.12 }}
          className="font-serif italic mt-4"
          style={{ fontSize: 'clamp(1.15rem, 2vw, 1.5rem)', color: colors.copper }}
        >
          Everyone deserves a Hudson.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          <a
            href={BOOKING_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={btnPrimary}
            style={{ backgroundColor: colors.copper }}
          >
            Book Your Consultation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <Link
            to="/membership"
            className={btnSecondary}
            style={{ borderColor: marketingBrand.white, color: marketingBrand.white }}
          >
            Discover Home Membership
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

const ServicesPage: React.FC = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
      <MetaTags
        title="DiscoverHome Membership | Hudson My Home"
        description={'A complete home management membership: a dedicated Hudson, a personalized MyHome Blueprint\u2122, the MyHome Score\u2122, and proactive seasonal care \u2014 all coordinated, never on your plate.'}
      />
      <Navbar />
      <main>
        <Hero />
        <SketchBreak />
        <MembershipIncludes />
        <WhoVisitsSection />
        <ServiceAreaSection />
        <ReinventedSection />
        <BlogSection />
        <FAQSection />
        <FooterCTA />
      </main>
      <Footer />
    </div>
  )
}

export default ServicesPage
